'use client';

import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';

import { Button } from '@/components/ui-kit/button';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import {
  useGetAgentById,
  useUpdateAgentPersona,
  useUpdateWidget,
} from '@/modules/gpt-chats/hooks/use-agents';
import { AgentImageUploader } from './agent-image-uploader';

import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Textarea } from '@/components/ui-kit/textarea';
import { editAgentDefaultValue, EditAgentForm, editAgentSchema } from './util';
import { IUpdateAgentPersonaPayload } from '@/modules/gpt-chats/types/agent.service.type';
import { Info } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';

type EditAgentModalProps = {
  agentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const EditAgentModal = ({ agentId, open, onOpenChange }: EditAgentModalProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAgentById(agentId);
  const { isPending, mutateAsync } = useUpdateAgentPersona();
  const { mutateAsync: updateWidget } = useUpdateWidget();
  const form = useForm<EditAgentForm>({
    resolver: zodResolver(editAgentSchema),
    defaultValues: editAgentDefaultValue,
    values: data?.agent
      ? {
          name: data.agent.name || '',
          description: data.agent.description || '',
          agent_type: data.agent.is_any_agent || false,
        }
      : undefined,
  });

  const submitHandler = async (values: EditAgentForm) => {
    try {
      if (!data?.agent || !agentId) return;

      const updatePayload: IUpdateAgentPersonaPayload = {
        agent_id: agentId,
        agent_type: '',
        name: values.name,
        language: '',
        tags: data.agent.tags ?? [],
        domain_expertise: data.agent.domain_expertise ?? [],
        role: data.agent.role ?? '',
        description: values.description ?? '',
        logo_url: data.agent.logo_url || '',
        logo_id: data.agent.logo_id || '',
        response_style: data.agent.response_style,
        formality_level: data.agent.formality_level,
        technical_level: data.agent.technical_level,
        is_any_agent: values.agent_type,
      };

      const res = await mutateAsync(updatePayload);
      if (!res.is_success)
        return showErrorToast({ errors: ErrorTransformer(res.detail || res.error) });

      await updateWidget({
        agent_id: agentId,
        regen_widget_id: false,
        widget: {
          ...data?.agent?.widget,
          name: values.name,
          greeting: t('GREETING_TEMPLATE', { name: values.name }),
        },
      });

      showSuccessToast({ description: t('AGENT_UPDATED_SUCCESSFULLY') });
      onOpenChange(false);
      form.reset();
    } catch (error) {
      return showErrorToast({ errors: ErrorTransformer(error) });
    }
  };

  const { control, handleSubmit, formState } = form;
  const { isDirty } = formState;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) form.reset();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('EDIT_AGENT')}</DialogTitle>
          <DialogDescription>{t('EDIT_AGENT_DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        {isLoading || !data?.agent ? (
          <div className="flex h-40 items-center justify-center">
            <div className="text-muted-foreground">{t('LOADING')}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[auto_1fr]">
            <div className="mt-4 sm:mt-2">
              <AgentImageUploader agent={data.agent} />
            </div>
            <div className="mt-4">
              <Form {...form}>
                <form onSubmit={handleSubmit((value) => submitHandler(value))}>
                  <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
                    <FormField
                      control={control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="lg:col-span-2">
                          <FormLabel>
                            {t('AGENT_NAME')} <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('NAME')}
                              {...field}
                              onBlur={(e) => field.onChange(e.target.value.trim())}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <FormItem className="lg:col-span-2">
                          <FormLabel className="flex items-center gap-2">
                            {t('DESCRIPTION')} <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t('DESCRIPTION')}
                              rows={4}
                              {...field}
                              value={field.value ?? ''}
                              onBlur={(e) => field.onChange(e.target.value.trim())}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="agent_type"
                      render={({ field }) => (
                        <FormItem className="lg:col-span-2">
                          <FormLabel className="text-base font-normal">{t('AGENT_TYPE')}</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={(val) => field.onChange(val === 'true')}
                              value={field.value ? 'true' : 'false'}
                              className="flex gap-6"
                            >
                              <FormItem className="space-y-0">
                                <div className="flex cursor-pointer items-center space-x-2">
                                  <FormControl>
                                    <RadioGroupItem value="false" id="sequential-minimal" />
                                  </FormControl>
                                  <FormLabel
                                    htmlFor="sequential-minimal"
                                    className="flex cursor-pointer flex-row items-center gap-2 font-normal"
                                  >
                                    {t('AGENT_TYPE_SEQUENTIAL_NAME')}
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('AGENT_TYPE_SEQUENTIAL_DESCRIPTION')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </FormLabel>
                                </div>
                              </FormItem>

                              <FormItem className="space-y-0">
                                <div className="flex cursor-pointer items-center space-x-2">
                                  <FormControl>
                                    <RadioGroupItem value="true" id="reasoning-minimal" />
                                  </FormControl>
                                  <FormLabel
                                    htmlFor="reasoning-minimal"
                                    className="flex cursor-pointer flex-row items-center gap-2 font-normal"
                                  >
                                    {t('AGENT_TYPE_REASONING_NAME')}
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('AGENT_TYPE_REASONING_DESCRIPTION')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </FormLabel>
                                </div>
                              </FormItem>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        onOpenChange(false);
                        form.reset();
                      }}
                    >
                      {t('CANCEL')}
                    </Button>
                    <Button type="submit" disabled={isPending || !isDirty}>
                      {t('SAVE')}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
